import { FLOW_STEP_TYPES } from "./campaignFlowTypes";
import { camelCaseToSentenceCase } from "./stringUtils";
import { toCalendarDate } from "./dates";

const addCampaignStepToFlow = (
  campaignState,
  campaignData,
  stepName,
  campaignStep
) => {
  const { stepType } = campaignState.campaignFlow.steps.find(
    step => step.name === stepName
  );

  const pushCampaignIds = key => {
    if (!campaignData.relations.campaignFlows[key]) {
      campaignData.relations.campaignFlows[key] = [];
    }
    campaignData.relations.campaignFlows[key].push(campaignStep.campaignStepId);
  };

  if (stepType === "required") {
    pushCampaignIds("requiredSteps");
  } else if (stepType === "optional") {
    pushCampaignIds("optionalSteps");
  }
};

export async function gatherCampaignData(
  campaignState,
  contractId,
  companyId,
  createCampaignStep
) {
  const campaignData = {
    contractId,
    ...campaignState.details,
    startDate: toCalendarDate(campaignState.details.startDate),
  };

  if (campaignState.product?.productId) {
    campaignData.productId = campaignState.product.productId;
  } else if (campaignState.product) {
    const { product } = campaignState;
    const productData = {
      name: product.name,
      categoryId: product.category[1],
      description: product.description,
      shortDescription: product.shortDescription,
      fullImageUrl: product.picture.fullImageUrl,
      thumbnailUrl: product.picture.thumbnailUrl,
      code: product.code,
      companyId,
      productPlatformId: product.productPlatformId,
      defaultPrice: product.productPrices.productPrice,
      relations: {
        productPrices: product.productPrices,
      },
    };
    campaignData.relations = {
      products: productData,
    };
  }

  if (campaignState.campaignRequirements?.campaignRequirementId) {
    campaignData.campaignRequirementId =
      campaignState.campaignRequirements.campaignRequirementId;
  } else if (campaignState.campaignRequirements) {
    const { campaignRequirements } = campaignState;
    const { photoExamples } = campaignRequirements;
    delete campaignRequirements.photoExamples;
    campaignData.relations = {
      ...campaignData.relations,
      campaignRequirements: {
        ...campaignRequirements,
        exampleFullUrls: photoExamples.fullMediaUrls,
        exampleLabels: photoExamples.labels,
      },
    };
  }

  if (campaignState.campaignFlow?.campaignFlowId) {
    campaignData.defaultFlowId = campaignState.campaignFlow.campaignFlowId;
  } else if (campaignState.campaignFlow) {
    const { campaignFlow } = campaignState;
    campaignData.relations = {
      ...campaignData.relations,
      campaignFlows: {
        name: campaignFlow.name,
      },
    };
  }

  const newlyCreatedEmailTemplates = [];

  // Process all flow steps
  for (const { name } of FLOW_STEP_TYPES) {
    const stepKey = `${name}Step`;

    if (campaignState[stepKey]?.campaignStepId) {
      addCampaignStepToFlow(
        campaignState,
        campaignData,
        name,
        campaignState[stepKey]
      );
    } else if (campaignState[stepKey]) {
      const campaignStep = await createCampaignStep({
        stepName: stepKey,
        stepType: name,
        newlyCreatedEmailTemplates,
      });
      addCampaignStepToFlow(campaignState, campaignData, name, campaignStep);
    }
  }

  return campaignData;
}

const generateStepDetails = ({
  campaignState,
  allData,
  updateStep,
  campaignData,
  stepType,
  stepNumber,
}) => {
  const stepName = camelCaseToSentenceCase(stepType);

  const getStepData = () => {
    if (campaignState[stepType]?.campaignStepId) {
      const currentStep =
        allData.campaignSteps[campaignState[stepType].campaignStepId];
      const stepData = {
        ...currentStep,
        emailTemplateFlow: currentStep.emailTemplateFlow.map(
          templateId => allData.emailTemplates[templateId].name
        ),
      };

      // Map metadata.regex to appropriate tag field based on step type
      if (stepType === "instagramStep") {
        stepData.instaTags = currentStep.metadata?.regex;
      } else if (stepType === "tiktokStep") {
        stepData.tags = currentStep.metadata?.regex;
      }

      return stepData;
    }

    if (campaignState.orderStep) {
      const currentStep = campaignState[stepType];
      return {
        ...currentStep,
        emailTemplateFlow: currentStep.emailTemplateFlow.map(
          template => template.name
        ),
      };
    }
    return null;
  };

  const step = getStepData();
  if (!step) return;

  campaignData[stepName] = {
    [`${stepName} Name`]: step.name,
    "Max Reminders": step.maxReminders,
    "Hours For Reminders": step.hoursForReminder,
    "Email Template Flow": step.emailTemplateFlow,
    edit: () => updateStep(stepNumber),
  };

  if (stepType === "instagramStep" && step.instaTags) {
    campaignData[stepName]["Instagram Tags"] = step.instaTags;
  }

  if (stepType === "tiktokStep" && step.tags) {
    campaignData[stepName]["TikTok Tags"] = step.tags;
  }
};

export function generateCampaignDetails(campaignState, allData, updateStep) {
  const campaignData = {};
  let currentStep = -1;

  if (campaignState.product?.productId) {
    const step = ++currentStep;
    const product = allData.products[campaignState.product.productId];
    campaignData.Product = {
      Name: product.name,
      Price: `$${product.defaultPrice}`,
      Code: product.code,
      Description: product.description,
      "Short Description": product.shortDescription,
      Image: product.thumbnailUrl,
      edit: () => updateStep(step),
    };
  } else if (campaignState.product) {
    const step = ++currentStep;
    const { product } = campaignState;
    const { productPrices } = product;
    campaignData.Product = {
      Name: product.name,
      Platform: allData.productPlatforms[product.productPlatformId].name,
      Category:
        product.category &&
        product.category[0] &&
        `${allData.categories[product.category[0]].name}/${
          allData.categories[product.category[0]].children[product.category[1]]
            .name
        }`,
      "Product Price": `$${productPrices.productPrice}`,
      "Service Fee": `$${productPrices.serviceFee}`,
      "Service Fee Description": `$${productPrices.serviceFeeDescription}`,
      Code: product.code,
      Description: product.description,
      "Short Description": product.shortDescription,
      Image: product.picture?.thumbnailUrl,
      edit: () => updateStep(step),
    };
  }

  if (campaignState.details) {
    const step = ++currentStep;
    const { details } = campaignState;
    campaignData["Campaign Details"] = {
      "Campaign Name": details.campaignName,
      Target: details.target,
      Link: details.link,
      "Start Date": details.startDate.format("MMM Do YYYY"),
      "Deadline Days": details.deadlineDays,
      edit: () => updateStep(step),
    };
  }

  if (campaignState.campaignRequirements) {
    const step = ++currentStep;
    const { campaignRequirements } = campaignState;
    campaignData["Campaign Requirements"] = {
      Name: campaignRequirements.name,
      "Photo Examples":
        campaignRequirements.photoExamples?.fullMediaUrls ||
        campaignRequirements.exampleFullUrls,
      "Example Labels":
        campaignRequirements.photoExamples?.labels ||
        campaignRequirements.exampleLabels,
      Tags: campaignRequirements.tags,
      ...campaignRequirements.form,
      edit: () => updateStep(step),
    };
  }

  if (campaignState.campaignFlow?.campaignFlowId) {
    const step = ++currentStep;
    const campaignFlow =
      allData.campaignFlows[campaignState.campaignFlow.campaignFlowId];
    campaignData["Campaign Flow"] = {
      "Campaign Flow Name": campaignFlow.name,
      edit: () => updateStep(step),
    };
  } else if (campaignState.campaignFlow) {
    const step = ++currentStep;
    const { campaignFlow } = campaignState;
    campaignData["Campaign Flow"] = {
      "Campaign Flow Name": campaignFlow.name,
      edit: () => updateStep(step),
    };
  }

  // Generate step details for all flow steps
  FLOW_STEP_TYPES.forEach(({ name }) => {
    const stepKey = `${name}Step`;
    if (campaignState[stepKey]) {
      generateStepDetails({
        campaignState,
        allData,
        updateStep,
        campaignData,
        stepType: stepKey,
        stepNumber: ++currentStep,
      });
    }
  });

  return campaignData;
}

export function buildStepCreationRequest(
  step,
  stepType,
  newlyCreatedEmailTemplates
) {
  const { emailTemplateFlow } = step;

  const stepRequest = {
    name: step.name,
    stepType,
    maxReminders: step.maxReminders,
    hoursForReminder: step.hoursForReminder,
  };

  if (stepType === "instagram" && step.instaTags?.length > 0) {
    stepRequest.metadata = {
      regex: step.instaTags,
    };
  }

  if (stepType === "tiktok" && step.tags?.length > 0) {
    stepRequest.metadata = {
      regex: step.tags,
    };
  }

  for (let i = 0; i < emailTemplateFlow.length; i++) {
    const emailTemplate = newlyCreatedEmailTemplates.find(
      template => template.name === emailTemplateFlow[i].name
    );
    if (emailTemplate) {
      emailTemplateFlow[i].emailTemplateId = emailTemplate.emailTemplateId;
    }
  }

  const existingEmailTemplateIds = emailTemplateFlow
    .filter(template => !!template.emailTemplateId)
    .map(template => template.emailTemplateId);

  if (existingEmailTemplateIds.length > 0) {
    stepRequest.emailTemplateFlow = existingEmailTemplateIds;
  }

  const newEmailTemplates = emailTemplateFlow
    .filter(template => !template.emailTemplateId)
    .map(template => {
      const mappedTemplate = {
        ...template,
        onboard_variables: {
          "%ID%": "onboardId",
          "%DEADLINE_DATE%": "deadlineDate",
        },
        campaign_variables: {
          "%CAMPAIGN_LINK%": "link",
          "%CAMPAIGN_ID%": "campaignId",
          "%CONTRACT_ID%": "contractId",
        },
      };
      if (stepType === "review") {
        mappedTemplate.template = "d-6016e55887f94c6ba06c7fa1f182c3b4";
      }
      return mappedTemplate;
    });

  if (newEmailTemplates.length > 0) {
    stepRequest.relations = {
      emailTemplates: newEmailTemplates,
    };
  }

  const stepsCreationRequest = [stepRequest];
  return stepsCreationRequest;
}
